import React from 'react';
import { observer } from 'mobx-react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    Grid,
    CircularProgress,
} from '@material-ui/core';
import BillingContactForm from './BillingContactForm';

const BillingDialog = observer(({
    open,
    loading,
    pendingPackage,
    billingForm,
    company,
    estimate,
    cbCustomer,
    originalCBEmail,
    originalCBFirstName,
    originalCBLastName,
    onClose,
    onCustomBilling,
    onNormalBilling,
    onBillingContactChange,
    onRestoreChargebeeData,
}) => {
    if (!open) return null;

    const estimatePrice =
        estimate != null && typeof estimate === 'object' && 'price' in estimate
            ? estimate.price
            : estimate;
    const estimatedPrice =
        estimatePrice != null
            ? (typeof estimatePrice === 'number'
                  ? estimatePrice.toFixed(2)
                  : estimatePrice)
            : estimatePrice;
    const futureCredit =
        estimate != null &&
        typeof estimate === 'object' &&
        'futureCredit' in estimate
            ? estimate.futureCredit
            : 0;
    const autoCollection = cbCustomer?.auto_collection === 'on' ? 'on' : 'off';
    const cbEmail = cbCustomer?.email || '';
    const cbExists = !!cbCustomer;
    const cbHasEmail = !!cbEmail;
    const managers = (company?.managers || []).map((m) => ({ email: m.email, name: m.name }));
    
    // Check if company already has custom billing enabled
    const isCurrentlyCustomBilling = (company?.settings || []).some(s => s.name === 'billing.custom' && s.value === '1');
    
    // Check if email has error for button disabling
    const contactMode = billingForm.$('contactMode').value;
    const emailHasError = contactMode !== 'chargebee_initial' && !!(billingForm.$('email').error);

    // Get package details from pendingPackage
    const packageCode = pendingPackage?.subscription || '';
    const seats = pendingPackage?.packageUsers || 0;
    const validFrom = pendingPackage?.valid_from ? new Date(pendingPackage.valid_from).toLocaleDateString() : '';
    const validTo = pendingPackage?.valid_to ? new Date(pendingPackage.valid_to).toLocaleDateString() : '';

    // Check if Valid From date is in the future (compare dates only, not time)
    const validFromDate = pendingPackage?.valid_from ? new Date(pendingPackage.valid_from) : null;
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Reset time to start of day
    
    let isFutureDate = false;
    if (validFromDate) {
        const validFromDateOnly = new Date(validFromDate);
        validFromDateOnly.setHours(0, 0, 0, 0); // Reset time to start of day
        isFutureDate = validFromDateOnly > today;
    }

    const customColumn = (
        <Box 
            position="relative" 
            p={3} 
            style={{ 
                backgroundColor: '#faf5ff', 
                borderRadius: 12,
                border: '2px solid #ce93d8',
                height: '100%',
                minHeight: 200
            }}
        >
            <Box display="flex" alignItems="center" mb={2}>
                <Box 
                    style={{ 
                        backgroundColor: '#ba68c8', 
                        color: 'white',
                        borderRadius: '50%',
                        width: 32,
                        height: 32,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        marginRight: 12,
                        fontSize: 18,
                        fontWeight: 'bold'
                    }}
                >
                    C
                </Box>
                <Typography variant="h6" style={{ fontWeight: 700, color: '#8e24aa' }}>
                    Custom Billing
                </Typography>
            </Box>
            <ul style={{ margin: '0 0 0 18px', paddingLeft: 4, lineHeight: 1.8 }}>
                <li style={{ marginBottom: 8 }}>Company becomes Custom Billed (Sets company setting <code style={{ backgroundColor: '#f3e5f5', padding: '2px 6px', borderRadius: 4 }}>billing.custom=1</code>)</li>
                <li style={{ marginBottom: 8 }}>Creates Chargebee subscription for <strong>internal calculations only</strong></li>
                <li style={{ marginBottom: 8 }}>Auto-collection is <strong>OFF</strong> - no automatic charges</li>
                <li style={{ marginBottom: 8 }}>Customer email locked to <strong>sales@awarego.com</strong></li>
                <li style={{ marginBottom: 8 }}>No invoices or emails sent to customer</li>
                <li style={{ marginBottom: 8 }}>Subscription will be <strong>cancelled automatically</strong> on Valid To date</li>
            </ul>
        </Box>
    );

    const normalColumn = (
        <Box 
            position="relative" 
            p={3} 
            style={{ 
                backgroundColor: isCurrentlyCustomBilling ? '#fafafa' : '#f0f7ff', 
                borderRadius: 12,
                border: isCurrentlyCustomBilling ? '2px solid #e0e0e0' : '2px solid #90caf9',
                height: '100%',
                minHeight: 200,
                opacity: isCurrentlyCustomBilling ? 0.6 : 1
            }}
        >
            {isCurrentlyCustomBilling && (
                <Box 
                    position="absolute" 
                    top={0} 
                    left={0} 
                    right={0} 
                    bottom={0} 
                    bgcolor="rgba(255, 255, 255, 0.85)" 
                    zIndex={1}
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    borderRadius="10px"
                    style={{ backdropFilter: 'blur(2px)' }}
                >
                    <Box 
                        bgcolor="white" 
                        p={3} 
                        borderRadius={2} 
                        boxShadow={3} 
                        textAlign="center"
                        style={{ border: '2px solid #ffb74d', maxWidth: 280 }}
                    >
                        <Typography variant="h6" gutterBottom style={{ fontWeight: 700, color: '#f57c00' }}>
                            🔒 Disabled
                        </Typography>
                        <Typography variant="body2" style={{ marginBottom: 8 }}>
                            Custom billing is currently active
                        </Typography>
                        <Typography variant="caption" display="block" color="textSecondary">
                            To enable normal billing, remove <code>billing.custom</code> setting first
                        </Typography>
                    </Box>
                </Box>
            )}
            <Box display="flex" alignItems="center" mb={2}>
                <Box 
                    style={{ 
                        backgroundColor: isCurrentlyCustomBilling ? '#bdbdbd' : '#64b5f6', 
                        color: 'white',
                        borderRadius: '50%',
                        width: 32,
                        height: 32,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        marginRight: 12,
                        fontSize: 18,
                        fontWeight: 'bold'
                    }}
                >
                    N
                </Box>
                <Typography variant="h6" style={{ fontWeight: 700, color: isCurrentlyCustomBilling ? '#9e9e9e' : '#1976d2' }}>
                    Normal Billing
                </Typography>
            </Box>
            <ul style={{ margin: '0 0 0 18px', paddingLeft: 4, lineHeight: 1.8 }}>
                <li style={{ marginBottom: 8 }}>Company will <strong>not</strong> be marked as custom billing</li>
                <li style={{ marginBottom: 8 }}>Invoice sent to customer's billing email <strong>immediately</strong></li>
                {cbExists ? (
                    <li style={{ marginBottom: 8 }}>Billing contact changes will <strong>override</strong> existing Chargebee data</li>
                ) : (
                    <li style={{ marginBottom: 8 }}>Set up billing contact information below</li>
                )
                }
                <li style={{ marginBottom: 8 }}><strong>Valid To date is ignored</strong> - subscription renews monthly/yearly on Valid From date until manually cancelled</li>
                {cbExists ? (
                    autoCollection === 'on' ? (
                        <li style={{ marginBottom: 8 }}>Customer Auto-collection is currently <strong>ON</strong> meaning Chargebee will try to charge customer immediately</li>
                    ) : (
                        <li style={{ marginBottom: 8 }}>Customer Auto-collection is currently <strong>OFF</strong> meaning Chargebee will not try to charge customer immediately</li>
                    )
                ) : (
                    <li style={{ marginBottom: 8 }}>Set customer Auto-collection <strong>OFF</strong> since a new Chargebee customer</li>
                    )
                }
            </ul>
        </Box>
    );

    return (
        <Dialog open maxWidth="lg" fullWidth onClose={onClose}>
            <DialogTitle style={{ backgroundColor: '#f5f5f5', borderBottom: '2px solid #e0e0e0', padding: '24px' }}>
                <Typography variant="h5" style={{ fontWeight: 700, color: '#333' }}>
                    Confirm Package Creation
                </Typography>
                <Typography variant="body2" color="textSecondary" style={{ marginTop: 4 }}>
                    Choose billing type and configure subscription details
                </Typography>
            </DialogTitle>
            <DialogContent style={{ padding: '32px' }}>
                {loading ? (
                    <Box textAlign="center" py={4}>
                        <CircularProgress />
                        <Typography variant="body2" mt={2}>Loading estimate...</Typography>
                    </Box>
                ) : (
                    <Box>
                        <Box
                            mb={3}
                            p={3}
                            style={{
                                background: 'linear-gradient(135deg, #b7acd6 0%, #a596cc 100%)',
                                border: '2px solid rgba(113,88,148,0.75)',
                                borderRadius: 10,
                                textAlign: 'center',
                            }}
                        >
                            <Typography variant="caption" style={{ color: 'white', textTransform: 'uppercase', letterSpacing: 1, fontWeight: 600, display: 'block', marginBottom: 8, fontSize: 11 }}>
                                Estimated Price
                            </Typography>
                            <Box 
                                style={{ 
                                    padding: '8px 24px',
                                    backgroundColor: 'rgba(112,112,112,0.15)',
                                    borderRadius: 8,
                                    border: '1px solid rgba(255,255,255,0.3)',
                                    backdropFilter: 'blur(10px)',
                                    marginBottom: 12,
                                    display: 'inline-block'
                                }}
                            >
                                <Typography 
                                    style={{ 
                                        fontSize: 36, 
                                        fontWeight: 700, 
                                        color: 'white',
                                        textShadow: '0 2px 6px rgba(0,0,0,0.2)',
                                        lineHeight: 1
                                    }}
                                >
                                    ${estimatedPrice}
                                </Typography>
                            </Box>
                            {futureCredit > 0 ? (
                                <Typography
                                    variant="body2"
                                    style={{
                                        color: 'rgba(255,255,255,0.95)',
                                        marginBottom: 8,
                                        fontSize: 13,
                                    }}
                                >
                                    Proration credit:&nbsp;
                                    <span style={{ fontWeight: 600 }}>
                                        $
                                        {(futureCredit / 100).toFixed(0)}
                                    </span>
                                </Typography>
                            ) : null}
                            <Box style={{ display: 'block', marginBottom: 10 }}>
                                <Box 
                                    style={{ 
                                        display: 'inline-block',
                                        backgroundColor: 'rgba(112,112,112,0.15)',
                                        borderRadius: 8,
                                        padding: '10px 16px',
                                        border: '1px solid rgba(255,255,255,0.3)'
                                    }}
                                >
                                <Grid container spacing={2} alignItems="center" justifyContent="center">
                                    <Grid item>
                                        <Typography variant="caption" style={{ color: 'white', fontSize: 9, textTransform: 'uppercase', display: 'block', marginBottom: 2 }}>
                                            Package
                                        </Typography>
                                        <Typography variant="body2" style={{ color: 'white', fontWeight: 600, fontSize: 12 }}>
                                            {packageCode}
                                        </Typography>
                                    </Grid>
                                    <Grid item>
                                        <Box style={{ width: 1, height: 30, backgroundColor: 'rgba(255,255,255,0.4)' }} />
                                    </Grid>
                                    <Grid item>
                                        <Typography variant="caption" style={{ color: 'white', fontSize: 9, textTransform: 'uppercase', display: 'block', marginBottom: 2 }}>
                                            Seats
                                        </Typography>
                                        <Typography variant="body2" style={{ color: 'white', fontWeight: 600, fontSize: 12 }}>
                                            {seats}
                                        </Typography>
                                    </Grid>
                                    <Grid item>
                                        <Box style={{ width: 1, height: 30, backgroundColor: 'rgba(255,255,255,0.4)' }} />
                                    </Grid>
                                    <Grid item>
                                        <Typography variant="caption" style={{ color: 'white', fontSize: 9, textTransform: 'uppercase', display: 'block', marginBottom: 2 }}>
                                            Validity
                                        </Typography>
                                        <Typography variant="body2" style={{ color: 'white', fontWeight: 600, fontSize: 12 }}>
                                            {validFrom} - {validTo}
                                        </Typography>
                                    </Grid>
                                </Grid>
                                </Box>
                            </Box>
                            
                            {isFutureDate && (
                                <Box 
                                    mt={1}
                                    p={1.5}
                                    style={{ 
                                        backgroundColor: 'rgba(112,112,112,0.15)',
                                        borderRadius: 6,
                                        border: '1px solid #ffc107',
                                        display: 'inline-block',
                                        marginBottom: 12
                                    }}
                                >
                                    <Box display="flex" alignItems="center">
                                        <Box style={{ fontSize: 16, marginRight: 8, color: '#ff9800' }}>⚠️</Box>
                                        <Typography variant="caption" style={{ color: 'white', fontSize: 11, lineHeight: 1.4 }}>
                                            Valid From date set to future - subscription will start on selected date. Package will not be considered valid until this date.
                                        </Typography>
                                    </Box>
                                </Box>
                            )}

                            <Typography variant="caption" style={{ color: 'white', fontSize: 11, display: 'block', marginBottom: 0 }}>
                                Calculated estimate from Chargebee for this subscription
                            </Typography>
                        </Box>

                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                {cbExists ? (
                                    <Box 
                                        mb={2}
                                        p={3} 
                                        style={{ 
                                            backgroundColor: '#f1f8f4', 
                                            borderRadius: 12,
                                            border: '2px solid #81c784',
                                            boxShadow: '0 2px 8px rgba(129,199,132,0.15)'
                                        }}
                                    >
                                        <Box display="flex" alignItems="center" mb={2}>
                                            <Box 
                                                style={{
                                                    backgroundColor: '#66bb6a',
                                                    color: 'white',
                                                    borderRadius: '50%',
                                                    width: 40,
                                                    height: 40,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    marginRight: 12,
                                                    fontSize: 20,
                                                    fontWeight: 'bold'
                                                }}
                                            >
                                                ✓
                                            </Box>
                                            <Typography variant="h6" style={{ fontWeight: 700, color: '#388e3c' }}>
                                                Customer exists in Chargebee
                                            </Typography>
                                        </Box>
                                        <Grid container spacing={2}>
                                            <Grid item xs={12} sm={4}>
                                                <Typography variant="body2" color="textSecondary" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>
                                                    Current Email
                                                </Typography>
                                                <Typography variant="body1" style={{ fontWeight: 600 }}>
                                                    {cbEmail || '(no email)'}
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={12} sm={4}>
                                                <Typography variant="body2" color="textSecondary" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>
                                                    Current Auto-collection
                                                </Typography>
                                                <Typography variant="body1" style={{ fontWeight: 600 }}>
                                                    {autoCollection.toUpperCase()}
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={12} sm={4}>
                                                <Typography variant="body2" color="textSecondary" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>
                                                    Current Custom Billing
                                                </Typography>
                                                <Typography variant="body1" style={{ fontWeight: 600, color: '#000' }}>
                                                    {isCurrentlyCustomBilling ? 'ON' : 'OFF'}
                                                </Typography>
                                            </Grid>
                                        </Grid>
                                    </Box>
                                ) : (
                                    <Box 
                                        mb={2}
                                        p={3} 
                                        style={{ 
                                            backgroundColor: '#fff8f0', 
                                            borderRadius: 12,
                                            border: '2px solid #ffb74d',
                                            boxShadow: '0 2px 8px rgba(255,183,77,0.15)'
                                        }}
                                    >
                                        <Box display="flex" alignItems="center" mb={2}>
                                            <Box 
                                                style={{
                                                    backgroundColor: 'rgba(255,192,74,0.68)',
                                                    color: 'white',
                                                    borderRadius: '50%',
                                                    width: 40,
                                                    height: 40,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    marginRight: 12,
                                                    fontSize: 20,
                                                    fontWeight: 'bold'
                                                }}
                                            >
                                                !
                                            </Box>
                                            <Typography variant="h6" style={{ fontWeight: 700, color: '#e39038' }}>
                                                Customer is not in Chargebee
                                            </Typography>
                                        </Box>
                                        <Grid container spacing={2} mb={2}>
                                            <Grid item xs={12} sm={8}>
                                                <Typography variant="body2" style={{ lineHeight: 1.6 }}>
                                                    A new Chargebee customer will be created with the billing contact information you provide below.
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={12} sm={4}>
                                                <Typography variant="body2" color="textSecondary" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>
                                                    Current Custom Billing
                                                </Typography>
                                                <Typography variant="body1" style={{ fontWeight: 600, color: '#000' }}>
                                                    {isCurrentlyCustomBilling ? 'ON' : 'OFF'}
                                                </Typography>
                                            </Grid>
                                        </Grid>
                                    </Box>
                                )}
                            </Grid>

                            <Grid item xs={12} md={6}>{customColumn}</Grid>
                            <Grid item xs={12} md={6}>{normalColumn}</Grid>
                            
                            <Grid item xs={12}>
                                <BillingContactForm 
                                    form={billingForm}
                                    managers={managers}
                                    cbExists={cbExists}
                                    cbHasEmail={cbHasEmail}
                                    onContactModeChange={onBillingContactChange}
                                    originalCBData={{
                                        email: originalCBEmail,
                                        firstName: originalCBFirstName,
                                        lastName: originalCBLastName
                                    }}
                                    onRestoreChargebeeData={onRestoreChargebeeData}
                                    isCurrentlyCustomBilling={isCurrentlyCustomBilling}
                                />
                            </Grid>
                        </Grid>
                    </Box>
                )}
            </DialogContent>
            <DialogActions style={{ padding: '24px', backgroundColor: '#fafafa', borderTop: '1px solid #e0e0e0' }}>
                <Button 
                    onClick={onClose} 
                    color="default"
                    size="large"
                    style={{ marginRight: 'auto' }}
                >
                    Cancel
                </Button>
                <Button 
                    onClick={onCustomBilling} 
                    variant="contained"
                    size="large"
                    disabled={loading}
                    style={{ 
                        backgroundColor: loading ? undefined : '#ba68c8',
                        color: 'white',
                        fontWeight: 600,
                        padding: '10px 32px',
                        marginRight: 12
                    }}
                >
                    {loading ? <CircularProgress size={24} style={{ color: 'white' }} /> : 'Custom Billing'}
                </Button>
                <Button 
                    onClick={onNormalBilling} 
                    variant="contained"
                    size="large"
                    disabled={loading || isCurrentlyCustomBilling || emailHasError}
                    style={{ 
                        backgroundColor: (loading || isCurrentlyCustomBilling || emailHasError) ? undefined : '#64b5f6',
                        color: 'white',
                        fontWeight: 600,
                        padding: '10px 32px'
                    }}
                >
                    {loading ? <CircularProgress size={24} style={{ color: 'white' }} /> : 'Normal Billing'}
                </Button>
            </DialogActions>
        </Dialog>
    );
});

export default BillingDialog;
